import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Container,
    Typography,
    Box,
    Button,
    List,
    ListItem,
    ListItemText,
    Paper,
    Avatar,
    ListItemIcon,
    Fade,
    alpha,
    Link,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    TextField
} from "@mui/material";
import {
    Search as SearchIcon,
    Email as EmailIcon,
    PhoneAndroid as PhoneAndroidIcon,
    Person as PersonIcon,
    Language as LanguageIcon,
    Payment as PaymentIcon,
    LocationOn as LocationOnIcon,
    Groups as GroupsIcon,
    FitnessCenter as FitnessCenterIcon,
    Storefront as StorefrontIcon
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import PublicCompetitions from '../components/PublicCompetitions';
import PublicEvents from '../components/PublicEvents';
import { EmojiEvents, Event as EventIcon } from '@mui/icons-material';
import {
    fetchAffiliates,
    fetchAffiliateInfo,
    addHomeAffiliate,
    removeHomeAffiliate,
    fetchPlans,
    checkHomeAffiliate
} from "../api/getClassesApi";
import { getUserProfile } from "../api/profileApi";
import { getUserPlansByAffiliate } from "../api/profileApi";
import { getFamilyMembers } from "../api/familyApi";
import AppTheme from "../shared-theme/AppTheme";
import ShowPlans from "../components/ShowPlans";
import ContractRequestPlans from "../components/ContractRequestPlans";
import BaseContractPlans from "../components/BaseContractPlans";
import { getAffiliateContractRequestPlans } from "../api/contractRequestApi";
import { getAffiliateBaseContracts } from "../api/baseContractApi";
import { formatCurrency } from "../utils/currency";

const StyledContainer = styled(Container)(({ theme }) => ({
    padding: theme.spacing(1, 2),
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0),
    backgroundColor: theme.palette.background.default,
}));

const SearchResultItem = styled(ListItem)(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    transition: 'background-color 0.2s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0.5, 0),
}));

const RegisterTrainingPage = () => {
    const [searchQuery, setSearchQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [selectedAffiliate, setSelectedAffiliate] = useState(null);
    const [plans, setPlans] = useState([]);
    const [contractPlans, setContractPlans] = useState([]);
    const [baseContracts, setBaseContracts] = useState([]);
    const [isHomeGym, setIsHomeGym] = useState(false);
    const [userPlans, setUserPlans] = useState([]);
    const [userData, setUserData] = useState(null);
    const [viewingPlans, setViewingPlans] = useState(false);
    const [familyMembers, setFamilyMembers] = useState([]);
    const [showFamilyMemberDialog, setShowFamilyMemberDialog] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedFamilyMember, setSelectedFamilyMember] = useState("self");
    const [isLoading, setIsLoading] = useState(true);
    const [dateError, setDateError] = useState('');
    const [isPlanStartDate, setIsPlanStartDate] = useState(false);
    const [planStartDate, setPlanStartDate] = useState('');
    const [showCompetitions, setShowCompetitions] = useState(false);
    const [showEvents, setShowEvents] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Ensure training types are always in array format
    const ensureTrainingTypeArray = (trainingType) => {
        if (!trainingType) return [];
        if (Array.isArray(trainingType)) return trainingType;
        if (typeof trainingType === 'string') {
            try {
                const parsed = JSON.parse(trainingType);
                return Array.isArray(parsed) ? parsed : [trainingType];
            } catch (e) {
                return [trainingType];
            }
        }
        return [trainingType];
    };

    // Sort plans with special price logic
    const sortPlansWithSpecialPrice = (plansArray) => {
        return plansArray.sort((a, b) => {
            // Kui üks on special price ja teine mitte
            if (a.isSpecialPrice && !b.isSpecialPrice) return -1;
            if (!a.isSpecialPrice && b.isSpecialPrice) return 1;

            // Kui mõlemad on special price või kumbki pole, sorteeri hinna järgi
            return parseFloat(a.price || 0) - parseFloat(b.price || 0);
        });
    };

    useEffect(() => {
        const loadAffiliateFromRedirect = async () => {
            if (location.state?.affiliateId) {
                try {
                    const affiliateData = await fetchAffiliateInfo(location.state.affiliateId);
                    setSelectedAffiliate(affiliateData.affiliate);

                    if (location.state?.openPlans) {
                        await loadAffiliatePlans(affiliateData.affiliate);
                    }
                } catch (error) {
                    console.error("Error loading affiliate from redirect:", error);
                }
            }
        };

        loadAffiliateFromRedirect();
    }, [location.state]);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const profile = await getUserProfile();
                setUserData(profile);

                const members = await getFamilyMembers();
                setFamilyMembers(members);

                // Kui tuldi redirectist, siis ära automaatselt lae home gym affiliatet
                if (!location.state?.affiliateId) {
                    const homeAffiliateData = await checkHomeAffiliate();
                    if (homeAffiliateData.homeAffiliate) {
                        setIsHomeGym(true);
                        const affiliateData = await fetchAffiliateInfo(homeAffiliateData.homeAffiliate);
                        setSelectedAffiliate(affiliateData.affiliate);
                    }
                }
            } catch (error) {
                console.error("❌ Error fetching initial data:", error);
            }
        };

        loadInitialData();
        setTimeout(() => {
            setIsLoading(false);
        }, 500);
    }, [location.state?.affiliateId]);

    const getAffiliateInitials = (name) => {
        if (!name) return "??";
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const handleSearchChange = async (e) => {
        setSearchQuery(e.target.value);
        if (e.target.value.trim()) {
            const results = await fetchAffiliates(e.target.value.trim());
            const filteredResults = results.filter(affiliate =>
                affiliate.name !== "Crossfit Test"
            );
            setSuggestions(filteredResults);
        } else {
            setSuggestions([]);
        }
    };

    const handleAffiliateSelect = async (affiliate) => {
        const affiliateData = await fetchAffiliateInfo(affiliate.id);
        const affiliateInfo = affiliateData.affiliate;
        setSelectedAffiliate(affiliateInfo);

        const homeAffiliateData = await checkHomeAffiliate();
        setIsHomeGym(homeAffiliateData.homeAffiliate === affiliate.id);

        setPlans([]);
        setContractPlans([]);
        setBaseContracts([]);
        setSuggestions([]);
        setSearchQuery("");
    };

    const loadAffiliatePlans = async (affiliateToUse = null) => {
        const affiliate = typeof affiliateToUse === "object" && affiliateToUse !== null
            ? affiliateToUse
            : selectedAffiliate;

        if (!affiliate || !affiliate.id) {
            return;
        }
        try {
            const [affiliatePlans, offerings, affiliateBaseContracts, getUserPlans] = await Promise.all([
                fetchPlans(affiliate.ownerId),
                getAffiliateContractRequestPlans(affiliate.id).catch((error) => {
                    console.error("Error loading contract offerings:", error);
                    return [];
                }),
                getAffiliateBaseContracts(affiliate.id).catch((error) => {
                    console.error("Error loading base contracts:", error);
                    return [];
                }),
                getUserPlansByAffiliate(affiliate.id),
            ]);
            const filteredPlans = affiliatePlans.filter(plan => plan.active);

            const processedPlans = filteredPlans.map(plan => ({
                ...plan,
                trainingType: ensureTrainingTypeArray(plan.trainingType)
            }));

            // Sort plans with special price logic
            const sortedPlans = sortPlansWithSpecialPrice(processedPlans);
            setPlans(sortedPlans);
            setContractPlans(Array.isArray(offerings) ? offerings : []);
            setBaseContracts(Array.isArray(affiliateBaseContracts) ? affiliateBaseContracts : []);
            setUserPlans(getUserPlans);

            setViewingPlans(true);
        } catch (error) {
            console.error("❌ Error loading plans:", error);
        }
    };

    const handleBackToAffiliate = () => {
        setViewingPlans(false);
    };

    const handleAddHomeAffiliate = async () => {
        if (!selectedAffiliate || !selectedAffiliate.id) {
            return;
        }

        try {
            await addHomeAffiliate(selectedAffiliate.id);
            setIsHomeGym(true);
        } catch (error) {
            console.error("❌ Error adding home gym:", error);
        }
    };

    const handleRemoveHomeAffiliate = async () => {
        if (selectedAffiliate) {
            await removeHomeAffiliate(selectedAffiliate.id);
            setIsHomeGym(false);
        }
    };

    const handleOpenShop = () => {
        if (!selectedAffiliate?.id) return;
        navigate("/shop-checkout", {
            state: {
                affiliate: selectedAffiliate,
                userData
            }
        });
    };

    const handleBuyPlan = (plan) => {

            setSelectedPlan(plan);
            setShowFamilyMemberDialog(true);

    };

    const handleFamilyMemberSelection = (event) => {
        setSelectedFamilyMember(event.target.value);
    };

    const handleDialogConfirm = () => {
        // Clear previous error
        setDateError('');

        // Validate that if custom start date is enabled, a date must be selected
        if (isPlanStartDate && !planStartDate) {
            setDateError("Please select a start date or uncheck the custom start date option.");
            return;
        }

        const isFamilyMember = selectedFamilyMember !== "self";
        const familyMemberId = isFamilyMember ? selectedFamilyMember : null;

        proceedToCheckout(
            selectedPlan,
            isFamilyMember,
            familyMemberId,
            isPlanStartDate,
            isPlanStartDate ? (planStartDate || 0) : 0
        );
        setShowFamilyMemberDialog(false);
        setIsPlanStartDate(false);
        setPlanStartDate('');
    };

    const handleDialogCancel = () => {
        setSelectedFamilyMember("self");
        setIsPlanStartDate(false);
        setPlanStartDate('');
        setDateError(''); // Clear error when canceling
        setShowFamilyMemberDialog(false);
    };



    const proceedToCheckout = (plan, isFamilyMember, familyMemberId, useCustomStartDate = false, customPlanStartDate = 0) => {

        if (plan.affiliateId !== selectedAffiliate.id) {
            console.error(`Plan affiliateId (${plan.affiliateId}) doesn't match selected affiliate (${selectedAffiliate.id})`);
            alert("Error: Plan doesn't belong to selected affiliate. Please refresh and try again.");
            return;
        }

        navigate("/checkout", {
            state: {
                affiliate: selectedAffiliate,
                plan: plan,
                contract: false,
                userData: userData,
                familyMember: isFamilyMember,
                familyMemberId: familyMemberId,
                isContractPayment: false,
                isPlanStartDate: useCustomStartDate,
                planStartDate: customPlanStartDate,
            }
        });
    };

    function getValidUntil(planId, userPlans) {
        const foundPlans = userPlans.filter((up) => up.planId === planId);
        if (foundPlans.length === 0) {
            return null;
        }

        const now = new Date();
        const validPlans = foundPlans.filter(plan => {
            const endDate = new Date(plan.endDate);
            return endDate > now && plan.sessionsLeft > 0;
        });

        if (validPlans.length === 0) {
            return null;
        }

        const latestPlan = validPlans.reduce((latest, current) => {
            const latestDate = new Date(latest.endDate);
            const currentDate = new Date(current.endDate);
            return currentDate > latestDate ? current : latest;
        }, validPlans[0]);

        return formatDateISOtoDDMMYYYY(latestPlan.endDate);
    }

    function formatDateISOtoDDMMYYYY(isoDateString) {
        if (!isoDateString) return null;
        const d = new Date(isoDateString);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}.${month}.${year}`;
    }

    return (
        <AppTheme>
            <StyledContainer maxWidth="lg">
                {isLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            zIndex: 999
                        }}
                    >
                        <CircularProgress size={60} />
                    </Box>
                )}

                <Typography variant="h4" sx={{ fontWeight: 600, mt: 0, mb: 1 }}>
                    Register for Training
                </Typography>

                {/* Search Affiliate UI */}
                <Box sx={{ width: "100%", maxWidth: "600px", mb: 4 }}>
                    <Paper
                        elevation={2}
                        sx={{
                            borderRadius: 2,
                            transition: "all 0.3s ease",
                            '&:hover': {
                                boxShadow: 3
                            }
                        }}
                    >
                        <Box sx={{ p: 0.5, display: 'flex', alignItems: 'center' }}>
                            <SearchIcon sx={{ ml: 2, color: 'text.secondary' }} />
                            <Box
                                component="input"
                                placeholder="Search Affiliate..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                sx={{
                                    width: "100%",
                                    border: "none",
                                    p: 2,
                                    fontSize: "1rem",
                                    outline: "none",
                                    bgcolor: 'transparent'
                                }}
                            />
                        </Box>
                    </Paper>

                    {suggestions.length > 0 && (
                        <Fade in={suggestions.length > 0}>
                            <Paper
                                elevation={3}
                                sx={{
                                    mt: 1,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    maxHeight: 350,
                                    overflowY: 'auto'
                                }}
                            >
                                <List sx={{ p: 1 }}>
                                    {suggestions.map((affiliate, index) => (
                                        <React.Fragment key={affiliate.id}>
                                            <SearchResultItem
                                                button
                                                onClick={() => handleAffiliateSelect(affiliate)}
                                            >
                                                <ListItemIcon>
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: 'primary.main',
                                                            width: 40,
                                                            height: 40
                                                        }}
                                                    >
                                                        {getAffiliateInitials(affiliate.name)}
                                                    </Avatar>
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                                            {affiliate.name}
                                                        </Typography>
                                                    }
                                                />
                                                <FitnessCenterIcon color="action" sx={{ ml: 2 }} />
                                            </SearchResultItem>
                                            {index < suggestions.length - 1 && <Box component="hr" sx={{ border: 'none', borderTop: '1px solid', borderColor: 'divider', my: 0.5 }} />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Paper>
                        </Fade>
                    )}
                </Box>

                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            startIcon={<EventIcon />}
                            onClick={() => setShowEvents(!showEvents)}
                        >
                            Events
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            startIcon={<EmojiEvents />}
                            onClick={() => setShowCompetitions(!showCompetitions)}
                        >
                            Competitions
                        </Button>
                    </Grid>
                </Grid>

                {showEvents && (
                    <Box sx={{ mb: 4 }}>
                        <PublicEvents affiliateId={selectedAffiliate?.id} affiliate={selectedAffiliate} />
                    </Box>
                )}

                {showCompetitions && (
                    <Box sx={{ mb: 4 }}>
                        <PublicCompetitions
                            affiliateId={selectedAffiliate?.id}
                            userId={userData?.id}
                        />
                    </Box>
                )}

                {/* Affiliate Info */}
                {selectedAffiliate && !viewingPlans && (
                    <Paper
                        elevation={2}
                        sx={{
                            width: "100%",
                            maxWidth: "1000px",
                            borderRadius: 3,
                            overflow: 'visible'
                        }}
                    >
                        <Box sx={{ p: 0 }}>
                            <Box sx={{ textAlign: 'center', p: 3, pb: 1, pt: 0 }}>
                                <Avatar
                                    src={selectedAffiliate.logo || 'https://via.placeholder.com/120'}
                                    alt={selectedAffiliate.name}
                                    sx={{
                                        width: '120px',
                                        height: '120px',
                                        margin: 'auto',
                                        mb: 2,
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                        borderRadius: 0,
                                    }}
                                />
                                <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                                    {selectedAffiliate.name}
                                </Typography>

                                <Box sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: 2,
                                    flexWrap: "wrap",
                                    mb: 2,
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    width: { xs: '100%', sm: 'auto' }
                                }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => loadAffiliatePlans()}
                                        sx={{
                                            borderRadius: 2,
                                            width: { xs: '100%', sm: 'auto' }
                                        }}
                                    >
                                        Buy Plans
                                    </Button>

                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={() =>
                                            navigate("/classes", { state: { affiliate: selectedAffiliate } })
                                        }
                                        sx={{
                                            borderRadius: 2,
                                            width: { xs: '100%', sm: 'auto' }
                                        }}
                                    >
                                        View Classes
                                    </Button>

                                    {selectedAffiliate?.isShopActive === true && (
                                        <Button
                                            variant="contained"
                                            color="success"
                                            startIcon={<StorefrontIcon />}
                                            onClick={handleOpenShop}
                                            sx={{
                                                borderRadius: 2,
                                                width: { xs: '100%', sm: 'auto' }
                                            }}
                                        >
                                            Shop & Gift Cards
                                        </Button>
                                    )}

                                    {isHomeGym ? (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            onClick={handleRemoveHomeAffiliate}
                                            sx={{
                                                borderRadius: 2,
                                                width: { xs: '100%', sm: 'auto' }
                                            }}
                                        >
                                            Remove Home Gym
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleAddHomeAffiliate}
                                            sx={{
                                                borderRadius: 2,
                                                width: { xs: '100%', sm: 'auto' }
                                            }}
                                        >
                                            Add as Home Gym
                                        </Button>
                                    )}
                                </Box>

                                {(selectedAffiliate.website || selectedAffiliate.subdomain) && (
                                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 2 }}>
                                        {selectedAffiliate.website && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <LanguageIcon sx={{ color: "primary.main" }}/>
                                                <Link href={selectedAffiliate.website} target="_blank" underline="hover">
                                                    {selectedAffiliate.website}
                                                </Link>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center", mb: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Feedback link:</Typography>
                                    <Link href={`https://${selectedAffiliate.feedback}`} target="_blank" underline="hover">
                                        {selectedAffiliate.feedback}
                                    </Link>
                                </Box>

                                <Box component="hr" sx={{ border: 'none', borderTop: '1px solid', borderColor: 'divider', mx: -3 }} />
                            </Box>

                            {/* Contact and Details Section */}
                            <Grid container spacing={3} sx={{ p: 3, pt: 2 }}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonIcon fontSize="small" />
                                        Contact Information
                                    </Typography>

                                    <Grid container spacing={2}>
                                        {selectedAffiliate.email && (
                                            <Grid item xs={12}>
                                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                        <EmailIcon sx={{ color: "primary.main" }}/>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Email</Typography>
                                                            <Typography variant="body1">
                                                                {selectedAffiliate.email}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        )}

                                        {selectedAffiliate.phone && (
                                            <Grid item xs={12}>
                                                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                        <PhoneAndroidIcon sx={{ color: "primary.main" }}/>
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">Phone</Typography>
                                                            <Typography variant="body1">
                                                                {selectedAffiliate.phone}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        )}

                                        <Grid item xs={12}>
                                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                    <LocationOnIcon sx={{ color: "primary.main" }}/>
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary">Address</Typography>
                                                        <Typography variant="body1">
                                                            {selectedAffiliate.address}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <GroupsIcon fontSize="small" />
                                        Trainers
                                    </Typography>

                                    {selectedAffiliate.trainers && selectedAffiliate.trainers.length > 0 ? (
                                        <Grid container spacing={2}>
                                            {selectedAffiliate.trainers.map((trainerObj) => (
                                                <Grid item xs={12} sm={6} key={trainerObj.id}>
                                                    <Paper
                                                        elevation={0}
                                                        sx={{
                                                            p: 2,
                                                            bgcolor: 'background.default',
                                                            borderRadius: 2,
                                                            transition: 'all 0.3s',
                                                            '&:hover': {
                                                                boxShadow: 1,
                                                                transform: 'translateY(-2px)'
                                                            }
                                                        }}
                                                    >
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                            <Avatar
                                                                src={trainerObj.trainer?.logo}
                                                                sx={{
                                                                    bgcolor: 'primary.main',
                                                                    width: 40,
                                                                    height: 40
                                                                }}
                                                            >
                                                                {(trainerObj.trainer?.fullName || trainerObj.trainer?.username || "?").charAt(0)}
                                                            </Avatar>
                                                            <Typography variant="body1" fontWeight={500}>
                                                                {trainerObj.trainer?.fullName || trainerObj.trainer?.username || "Unknown Trainer"}
                                                            </Typography>
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <Paper elevation={0} sx={{ p: 3, bgcolor: 'background.default', borderRadius: 2, textAlign: 'center' }}>
                                            <Typography color="text.secondary">No trainers assigned to this affiliate.</Typography>
                                        </Paper>
                                    )}
                                </Grid>

                                {selectedAffiliate.paymentHolidayFee && (
                                    <Grid item xs={12}>
                                        <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                    <PaymentIcon sx={{ color: "primary.main" }}/>
                                                    <Typography variant="body1">Payment Holiday Fee:</Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        px: 2,
                                                        py: 0.5,
                                                        bgcolor: 'success.main',
                                                        color: 'success.contrastText',
                                                        borderRadius: 1,
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {formatCurrency(selectedAffiliate.paymentHolidayFee, selectedAffiliate.paymentCurrency)}
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </Paper>
                )}

                {/* Plans Section */}
                {viewingPlans && (
                    <Box sx={{ mt: 4, width: "100%", textAlign: "center" }}>
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 3 }}>
                            <Button
                                variant="outlined"
                                onClick={handleBackToAffiliate}
                                sx={{ borderRadius: 2, mr: 3 }}
                            >
                                Back to Affiliate
                            </Button>
                            <Typography
                                component="h2"
                                variant="h4"
                                sx={{
                                    color: "text.primary",
                                    fontWeight: "bold",
                                }}
                            >
                                Available Plans
                            </Typography>
                        </Box>

                        <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
                            One-time purchase plans
                        </Typography>
                        <ShowPlans
                            plans={plans}
                            userPlans={userPlans}
                            onBuyPlan={handleBuyPlan}
                            getValidUntil={getValidUntil}
                            paymentCurrency={selectedAffiliate?.paymentCurrency || "EUR"}
                        />
                        <BaseContractPlans
                            offerings={baseContracts}
                            userData={userData}
                            paymentCurrency={selectedAffiliate?.paymentCurrency || "EUR"}
                        />
                        <ContractRequestPlans
                            offerings={contractPlans}
                            userData={userData}
                            paymentCurrency={selectedAffiliate?.paymentCurrency || "EUR"}
                        />
                    </Box>
                )}

                {/* Family Member Selection Dialog */}
                <Dialog open={showFamilyMemberDialog} onClose={handleDialogCancel}>
                    <DialogTitle>Select Plan Recipient</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" gutterBottom sx={{ my: 2 }}>
                            Please select who this plan is for:
                        </Typography>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel id="family-member-select-label">Recipient</InputLabel>
                            <Select
                                labelId="family-member-select-label"
                                id="family-member-select"
                                value={selectedFamilyMember}
                                label="Recipient"
                                onChange={handleFamilyMemberSelection}
                            >
                                <MenuItem value="self">{userData?.fullName || "Yourself"}</MenuItem>
                                {familyMembers.map((member) => (
                                    <MenuItem key={member.id} value={member.id}>
                                        {member.fullName}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>
                                Select for whom you're purchasing this plan.
                            </FormHelperText>
                        </FormControl>

                        {/* Add checkbox for plan start date */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isPlanStartDate}
                                    onChange={(e) => setIsPlanStartDate(e.target.checked)}
                                    name="planStartDateCheckbox"
                                />
                            }
                            label="Set custom start date for plan"
                            sx={{ mt: 2 }}
                        />

                        {/* Add date picker that shows only when checkbox is checked */}
                        {isPlanStartDate && (
                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <TextField
                                    id="plan-start-date"
                                    label="Plan Start Date"
                                    type="date"
                                    value={planStartDate}
                                    onChange={(e) => {
                                        setPlanStartDate(e.target.value);
                                        if (dateError) setDateError(''); // Clear error when user selects date
                                    }}
                                    error={!!dateError}
                                    helperText={dateError}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    inputProps={{
                                        min: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                                    }}
                                />
                            </FormControl>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Button onClick={handleDialogCancel} color="secondary">
                            Cancel
                        </Button>
                        <Button onClick={handleDialogConfirm} color="primary" variant="contained">
                            Continue
                        </Button>
                    </DialogActions>
                </Dialog>
            </StyledContainer>
        </AppTheme>
    );
}

export default RegisterTrainingPage;